<template>
  <UModal
    v-if="isOpen"
    v-model:open="isOpen"
    class="w-full sm:max-w-md"
    :ui="{
      overlay: 'bg-gray-950/75 dark:bg-gray-950/90 z-[60]',
      header: 'flex items-center justify-between p-3 sm:px-3 min-h-12 bg-[#e9edf3] dark:bg-(--ui-bg-elevated)',
      content: 'z-[60]',
    }"
  >
    <template #header>
      <VisuallyHidden>
        <DialogTitle>{{ t('rejectModal.title') }}</DialogTitle>
        <DialogDescription>{{ t('rejectModal.title') }}</DialogDescription>
      </VisuallyHidden>

      <div class="flex items-center justify-between w-full">
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-semibold leading-6 text-(--ui-text)">
            {{ t('rejectModal.title') }}
          </h3>
        </div>

        <UButton
          color="gray"
          variant="ghost"
          icon="i-heroicons-x-mark"
          class="ml-2 cursor-pointer"
          tabindex="-1"
          @click="isOpen = false"
        />
      </div>
    </template>

    <template #body>
      <div class="pb-4">
        <div v-if="isQuotation" class="space-y-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ t('rejectModal.reasonLabel') }}
          </label>
          <UTextarea
            v-model="reason"
            :placeholder="t('rejectModal.reasonPlaceholder')"
            :rows="3"
            :maxlength="200"
            class="w-full"
          />
        </div>

        <div v-else class="space-y-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ t('rejectModal.initiatorLabel') }}
          </label>
          <USelect
            v-model="selectedInitiator"
            :items="initiatorOptions"
            :placeholder="t('rejectModal.initiatorPlaceholder')"
            value-key="value"
            class="w-full"
            :ui="{ content: 'z-[100]' }"
          />
        </div>
      </div>

      <div class="flex justify-end space-x-2">
        <UButton color="neutral" variant="outline" @click="isOpen = false" :disabled="loading">
          {{ t('rejectModal.cancel') }}
        </UButton>
        <UButton color="error" @click="confirm" :loading="loading" :disabled="isQuotation ? !reason.trim() : !selectedInitiator">
          {{ t('rejectModal.confirm') }}
        </UButton>
      </div>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Initiator } from "@/api/eOrdersApi.ts"
import { DialogDescription, DialogTitle, VisuallyHidden } from "reka-ui"

const props = defineProps<{
  loading?: boolean
  isQuotation?: boolean
}>()

const emit = defineEmits<{
  'confirmQuotation': [reason: string]
  'confirmPurchaseOrder': [initiator: Initiator]
}>()

const { t } = useI18n()

const isOpen = defineModel<boolean>({ default: false })
const reason = ref('')
const selectedInitiator = ref<Initiator>()

const initiatorOptions = computed(() => [
  { label: t('rejectModal.initiatorSupplier'), value: Initiator.Supplier },
  { label: t('rejectModal.initiatorClient'), value: Initiator.Client }
])

function confirm() {
  if (props.isQuotation) {
    emit('confirmQuotation', reason.value.trim())
  } else {
    emit('confirmPurchaseOrder', selectedInitiator.value!)
  }
}

watch(isOpen, (newVal) => {
  if (newVal) {
    reason.value = ''
    selectedInitiator.value = undefined
  }
})
</script>
